import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Play, Plus, Trash2 } from 'lucide-react';
import type { ScheduledActivity } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';
import {
  allScheduledOptions,
  errorMessage,
  type ScheduledActivityListItem,
} from '@/features/desks/desks-queries';
import {
  humanizeRecurrence,
  ScheduledDialog,
  scheduledBodyFrom,
} from '@/features/desks/scheduled-dialog';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { normalizeText, SettingsBreadcrumb, SettingsSearch } from '@/features/settings/settings-ui';

/**
 * /settings/desks/scheduled — atividades agendadas de TODAS as mesas: tabela
 * Nome | Título do ticket | Recorrência | Próximo disparo | Mesa, switch de
 * ativo e executar agora/editar/excluir. A ordem é a do servidor (mesa asc);
 * a busca só filtra.
 */
export function ScheduledSettingsPage() {
  const queryClient = useQueryClient();
  const activities = useQuery(allScheduledOptions());
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<{ activity: ScheduledActivityListItem | null } | null>(
    null
  );
  const [feedback, setFeedback] = useState<string | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['erp', 'scheduled'] });

  const toggle = useMutation({
    mutationFn: async (a: ScheduledActivityListItem) => {
      // Linha FRESCA antes do PUT: o corpo substitui o registro inteiro — um
      // body montado do cache apagaria edições feitas por outro usuário.
      const rows = await queryClient.fetchQuery({ ...allScheduledOptions(), staleTime: 0 });
      const fresh = rows.find((row) => row.id === a.id);
      if (!fresh) throw new Error('Atividade não encontrada — atualize a lista.');
      const res = await api<ScheduledActivity>(`/scheduled-activities/${a.id}`, {
        method: 'PUT',
        body: scheduledBodyFrom(fresh, !a.active),
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/scheduled-activities/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const run = useMutation({
    mutationFn: async (a: ScheduledActivityListItem) => {
      const res = await api<unknown>(`/scheduled-activities/${a.id}/run`, { method: 'POST' });
      if (res.error) throw res.error;
      return a;
    },
    onSuccess: (a) => {
      setFeedback(`Disparo de "${a.name}" executado — o ticket foi criado.`);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['erp', 'tickets'] });
    },
  });

  // Haystack normalizado pré-computado por linha — a busca só filtra, sem
  // re-sortear (a ordem vem do servidor).
  const rows = useMemo(
    () =>
      (activities.data ?? []).map((a) => ({
        row: a,
        haystack: normalizeText(`${a.name} ${a.ticketTitle} ${a.deskName}`),
      })),
    [activities.data]
  );
  const q = normalizeText(query.trim());
  const list = useMemo(
    () => (q === '' ? rows : rows.filter((r) => r.haystack.includes(q))).map((r) => r.row),
    [rows, q]
  );

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb page="Atividades agendadas" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Atividades agendadas</h1>
        {activities.data ? (
          <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground tabular-nums">
            {activities.data.length}
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar atividade" />
          <Button size="sm" className="h-9" onClick={() => setDialog({ activity: null })}>
            <Plus className="size-4" strokeWidth={2.5} />
            Atividade agendada
          </Button>
        </div>
      </div>

      <div className="mt-4">
        {activities.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : activities.isError ? (
          <EmptyState
            message={`Erro ao carregar atividades: ${errorMessage(activities.error)}`}
          />
        ) : list.length === 0 ? (
          <EmptyState
            className="h-52"
            message={q !== '' ? 'Nenhuma atividade encontrada' : 'Não há dados'}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 px-4 font-semibold">Título do ticket</th>
                  <th className="h-10 px-4 font-semibold">Periodicidade</th>
                  <th className="h-10 px-4 font-semibold">Próximo disparo</th>
                  <th className="h-10 px-4 font-semibold">Mesa</th>
                  <th className="h-10 w-36 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr
                    key={a.id}
                    className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-56 truncate px-4 font-medium">{a.name}</td>
                    <td className="max-w-56 truncate px-4 text-muted-foreground">
                      {a.ticketTitle}
                    </td>
                    <td className="px-4 whitespace-nowrap">{humanizeRecurrence(a)}</td>
                    <td className="px-4 whitespace-nowrap text-muted-foreground">
                      {formatDateTime(a.nextRunAt)}
                    </td>
                    <td className="max-w-48 truncate px-4">{a.deskName}</td>
                    <td className="px-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <Switch
                          checked={a.active}
                          disabled={toggle.isPending}
                          onCheckedChange={() => toggle.mutate(a)}
                          aria-label={a.active ? `Desativar "${a.name}"` : `Ativar "${a.name}"`}
                        />
                        <DeskRowAction
                          label="Executar agora"
                          disabled={run.isPending}
                          onClick={() => {
                            setFeedback(null);
                            run.mutate(a);
                          }}
                        >
                          <Play className="size-3.5" />
                        </DeskRowAction>
                        <DeskRowAction
                          label="Editar atividade"
                          onClick={() => setDialog({ activity: a })}
                        >
                          <Pencil className="size-3.5" />
                        </DeskRowAction>
                        <DeskRowAction
                          label="Excluir atividade"
                          destructive
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Excluir a atividade "${a.name}"?`)) {
                              remove.mutate(a.id);
                            }
                          }}
                        >
                          <Trash2 className="size-3.5" />
                        </DeskRowAction>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {feedback ? <p className="mt-2 text-sm text-success">{feedback}</p> : null}
      {(toggle.error ?? remove.error ?? run.error) ? (
        <p className="mt-2 text-sm text-destructive">
          {errorMessage(toggle.error ?? remove.error ?? run.error)}
        </p>
      ) : null}

      {dialog ? (
        <ScheduledDialog activity={dialog.activity} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}
